import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  isAuthenticated,
  storeScreenCode,
  getScreenCode,
  getMasjidId,
} from "../../services/authService";
import { setCurrentScreenIdentifier } from "../../utils/screenStorage";
import { isHomeCode } from "../../utils/homeScreen";
import DisplayController from "../core/DisplayController";
import "./AuthScreen.css";

const AuthScreen: React.FC = () => {
  const [screenCode, setScreenCode] = useState("");
  const [masjidId, setMasjidId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisplay, setShowDisplay] = useState(false);
  const [displayScreenCode, setDisplayScreenCode] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for URL query parameters for auto-login
    const urlScreenCode =
      searchParams.get("screenCode") || searchParams.get("screen");
    const urlMasjidId =
      searchParams.get("masjidId") || searchParams.get("masjid");

    if (urlScreenCode && urlMasjidId) {
      // Auto-login with URL parameters - stay on same URL
      console.log(
        `Auto-login: screenCode=${urlScreenCode}, masjidId=${urlMasjidId}`
      );
      // Set the screen identifier for this tab first
      setCurrentScreenIdentifier(urlScreenCode.trim(), urlMasjidId.trim());
      // Store screen data (now namespaced)
      storeScreenCode(urlScreenCode.trim(), urlMasjidId.trim());
      // Show display directly without navigation
      setDisplayScreenCode(urlScreenCode.trim());
      setShowDisplay(true);
      return;
    }

    // Otherwise check if already authenticated for this tab
    if (isAuthenticated()) {
      const storedScreenCode = getScreenCode();
      const storedMasjidId = getMasjidId();
      if (isHomeCode(storedScreenCode)) {
        // This tab is a household tablet: back to the Home app, not its bare screen.
        navigate("/home", { replace: true });
      } else if (storedScreenCode && storedMasjidId) {
        // Navigate to display route for previously authenticated sessions
        navigate(`/display/${storedScreenCode}`);
      }
    }
  }, [navigate, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (!screenCode.trim()) {
        throw new Error("Screen code is required");
      }
      if (!masjidId.trim()) {
        throw new Error("Masjid ID is required");
      }

      // Set the screen identifier for this tab first
      setCurrentScreenIdentifier(screenCode.trim(), masjidId.trim());
      // Store both screen code and masjid ID (now namespaced)
      storeScreenCode(screenCode.trim(), masjidId.trim());

      // Update URL with query params and show display
      const newUrl = `${
        window.location.pathname
      }?screenCode=${encodeURIComponent(
        screenCode.trim()
      )}&masjidId=${encodeURIComponent(masjidId.trim())}`;
      window.history.replaceState({}, "", newUrl);
      setDisplayScreenCode(screenCode.trim());
      setShowDisplay(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect");
    } finally {
      setIsLoading(false);
    }
  };

  // If we have valid params, render the display directly
  if (showDisplay && displayScreenCode) {
    return <DisplayController screenCode={displayScreenCode} />;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img
            src="/logo192.png"
            alt="M247 Digital Signage"
            className="auth-logo"
          />
          <h2>Digital Signage Setup</h2>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="masjidId">Masjid ID</label>
            <input
              type="text"
              id="masjidId"
              value={masjidId}
              onChange={(e) => setMasjidId(e.target.value)}
              placeholder="Enter masjid ID"
              disabled={isLoading}
              className="form-control"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="screenCode">Screen Code</label>
            <input
              type="text"
              id="screenCode"
              value={screenCode}
              onChange={(e) => setScreenCode(e.target.value)}
              placeholder="Enter screen code"
              disabled={isLoading}
              className="form-control"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 mt-3"
            disabled={isLoading || !screenCode.trim() || !masjidId.trim()}
          >
            {isLoading ? "Connecting..." : "Connect Display"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AuthScreen;
